import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { AppRelease } from '../../shared/models/app-release';
import { config } from '../../../environments/environment';
import { isTauri } from '../../shared/helpers/platform';

/** Bridges the pre-Angular monitor into the zoneless application's signals. */
@Injectable({ providedIn: 'root' })
export class AppReleaseService {
  private readonly _availableRelease = signal<AppRelease | null>(null);
  readonly availableRelease = this._availableRelease.asReadonly();
  readonly displayVersion = computed(() => {
    const version = this.availableRelease()?.version;
    return version && version !== String(process.env['APP_VERSION']) ? version : undefined;
  });

  constructor() {
    // The native app updates through its store; release-monitor.js is off there.
    if (isTauri) return;
    // Local preview survives Reload because the flag stays in the URL. Leave
    // the real monitor untouched, and never enable fixtures in production.
    if (
      config.exposeDevTools &&
      ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname) &&
      new URLSearchParams(location.search).get('previewUpdate') === '1'
    ) {
      this._availableRelease.set({ revision: 'f'.repeat(40), version: '99.0.0' });
      return;
    }
    const receive = (event: WindowEventMap['andor:release-changed']) => this.receive(event.detail);
    window.addEventListener('andor:release-changed', receive);
    this.receive(window.ANDOR_RELEASE);
    inject(DestroyRef).onDestroy(() =>
      window.removeEventListener('andor:release-changed', receive)
    );
  }

  private receive(value: unknown) {
    if (value === null) {
      this._availableRelease.set(null);
      return;
    }
    if (!value || typeof value !== 'object' || !('revision' in value)) return;
    if (typeof value.revision !== 'string' || !/^[a-f0-9]{40,64}$/.test(value.revision)) return;
    const version =
      'version' in value && typeof value.version === 'string' && value.version.trim()
        ? value.version.trim()
        : undefined;
    const previous = this.availableRelease();
    if (previous?.revision === value.revision && previous.version === version) return;
    this._availableRelease.set({ revision: value.revision, version });
  }
}
